import type { ReactElement } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from './components/ErrorBoundary';
import { MainLayout } from '@/layouts/MainLayout';
import { useAuth } from './hooks';
import {
  LoginPage,
  ResetPasswordPage,
  VerifyEmailPage,
  NotFoundPage,
  HomePage,
  AccountPage,
  InfoPage,
  ApiDocsPage,
} from '@/routes';
import { MessagesPage } from '@/pages/MessagesPage';
import { MyEchoPage } from '@/features/my-echo/pages/MyEchoPage';
import { ToastProvider } from '@/context/ToastContext';
import { ToastContainer } from '@/components/ui/ToastContainer';
import { CreatorModal } from '@/features/account/components/CreatorModal';
import { DeleteAccountModal } from '@/features/account/components/delete-modal/DeleteAccountModal';
import { DeleteCreatorModal } from '@/features/account/components/delete-modal/DeleteCreatorModal';
import { CreateEchoPage } from '@/features/create-echo/pages/CreateEchoPage';
import { EchoProvider } from '@/context/EchoContext';
import { CreditsPage } from '@/features/credits/pages/CreditsPage';
import { AuthProvider } from '@/components/layout/AuthContext';
import { LogoutModal } from '@/features/auth/components/LogoutModal';
import { ComingSoonModal } from '@/features/create-echo/components/ComingSoonModal';
import { SupportModal } from '@/features/support/components/SupportModal';
import { PublicChatPage } from '@/features/public-chat/pages/PublicChatPage';
import { CustomPublicChatPage } from '@/features/public-chat/pages/CustomPublicChatPage';
import { TermsModal } from '@/features/auth/components/TermsModal';
import { queryClient } from '@/libs/queryClient';
import { EchoDetailsModal } from '@/components/chat/EchoDetailsModal';
import { PublicChatView } from '@/features/public-chat/components/PublicChatView';
import { NoCreditsModal } from '@/components/modals/NoCreditsModal';
import { StripeRequiredModal } from '@/components/modals/StripeRequiredModal';
import { AlertModal } from '@/components/modals/AlertModal';
import { WelcomeModal } from '@/components/modals/WelcomeModal';
import { LandingEchoPage } from '@/routes/landingecho';
import { getUtmEchoConfigBySlug } from '@/components/layout/auth/utmEchoConfig';
import { PaymentCancelPage, PaymentSuccessPage } from '@/pages/PaymentSuccessPage';
import { GOOGLE_CLIENT_ID } from '@/utils/socialAuthConfig';

// Public subdomain configuration - maps subdomains to echo configs
const PUBLIC_SUBDOMAIN_CONFIG: Record<string, { id: string; name: string }> = {
  algolymp: {
    id: 'f70ee590-c6aa-42f2-97a2-1bd100ebd56b',
    name: 'Vlad Munteanu',
  },
};

/**
 * Detect if current hostname is a public echo subdomain
 * OR if a query parameter is present (for local dev).
 */
function getPublicSubdomainConfig(): { id: string; name: string } | null {
  if (typeof window === 'undefined') return null;

  // 1. Dev Override: Check for ?public_echo=algolymp
  const params = new URLSearchParams(window.location.search);
  const overrideParam = params.get('public_echo');
  if (overrideParam) {
    const overrideKey = overrideParam as keyof typeof PUBLIC_SUBDOMAIN_CONFIG;
    const overrideCfg = PUBLIC_SUBDOMAIN_CONFIG[overrideKey];
    if (overrideCfg) return overrideCfg;
  }

  // 2. Production: Check Subdomain
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // Check for subdomain pattern: xxx.dechoes.com
  if (parts.length >= 3) {
    const subKey = parts[0] as keyof typeof PUBLIC_SUBDOMAIN_CONFIG;
    const subCfg = PUBLIC_SUBDOMAIN_CONFIG[subKey];
    if (subCfg) return subCfg;
  }

  return null;
}

/** Renders LandingEchoPage if the slug matches a UTM echo, otherwise NotFoundPage. */
function LandingEchoGuard() {
  const { echoSlug = '' } = useParams<{ echoSlug: string }>();
  if (!getUtmEchoConfigBySlug(echoSlug)) return <NotFoundPage />;
  return <LandingEchoPage />;
}

const REDIRECT_KEY = 'redirectAfterLogin';

/** Saves the attempted path to sessionStorage, then redirects to /login. */
function LoginRedirect() {
  const { pathname } = useLocation();
  if (pathname !== '/login') {
    sessionStorage.setItem(REDIRECT_KEY, pathname);
  }
  return <Navigate to='/login' replace />;
}

function DeveloperOnlyRoute({ children }: { children: ReactElement }) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return <LoginRedirect />;
  }

  if (!user.is_developer && user.role !== 'admin') {
    return <Navigate to='/account' replace />;
  }

  return children;
}

function AppRoutes() {
  const { isAuthenticated, isInitializing } = useAuth();

  // Show loading screen ONLY while initializing (checking token)
  if (isInitializing) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-background'>
        <div className='text-center'>
          <div className='mb-4 text-lg font-medium'>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path='/info' element={<InfoPage />} />

        {/* Protected route */}
        <Route path='/' element={isAuthenticated ? <MainLayout /> : <LoginRedirect />}>
          <Route index element={<Navigate to='/home' replace />} />
          <Route path='messages/group/:groupId' element={<MessagesPage />} />
          <Route path='messages/:echoName?' element={<MessagesPage />} />
          <Route path='home' element={<HomePage />} />
          <Route path='account' element={<AccountPage />} />
          <Route path='my-echo' element={<MyEchoPage />} />
          <Route path='create-echo' element={<CreateEchoPage />} />
          <Route path='credits' element={<CreditsPage />} />
          <Route
            path='api-docs'
            element={
              <DeveloperOnlyRoute>
                <ApiDocsPage />
              </DeveloperOnlyRoute>
            }
          />
          <Route path='payment/success' element={<PaymentSuccessPage />} />
          <Route path='payment/cancel' element={<PaymentCancelPage />} />
        </Route>

        {/* Landing/campaign page — matches UTM echo slugs (e.g. /socrates, /jesus) */}
        <Route path='/:echoSlug' element={<LandingEchoGuard />} />

        {/* Auth routes */}
        <Route path='/login' element={<LoginPage />} />
        <Route
          path='/reset-password'
          element={isAuthenticated ? <Navigate to='/' replace /> : <ResetPasswordPage />}
        />
        <Route path='/verify-email' element={<VerifyEmailPage />} />

        {/* Public chat route */}
        <Route path='/p/:pageSlug' element={<CustomPublicChatPage />} />
        <Route path='/chat/:echoName' element={<PublicChatPage />} />

        {/* Not Found route */}
        <Route path='*' element={<NotFoundPage />} />
      </Routes>

      {/* Toasts above everything */}
      <ToastContainer />
    </>
  );
}

function App() {
  // Check if we're on a public subdomain (e.g., algolymp.dechoes.com)
  const publicConfig = getPublicSubdomainConfig();
  const isCustomPublicPage =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/p/');

  // If on a public subdomain, render ONLY the legacy public chat. Custom
  // public pages must keep normal routing so /p/:pageSlug resolves metadata.
  if (publicConfig && !isCustomPublicPage) {
    const publicEcho = {
      id: publicConfig.id,
      name: publicConfig.name,
      category: 'Public Figure',
      domain: 'General',
      imageUrl: '',
    };

    return (
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <div className='h-dvh w-full overflow-hidden bg-black'>
            <PublicChatView echo={publicEcho} echoName={publicConfig.name} />
            <EchoDetailsModal />
          </div>
        </QueryClientProvider>
      </ErrorBoundary>
    );
  }

  const normalApp = (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <EchoProvider>
          <ToastProvider>
            <AppRoutes />
            {/* Modals Layer */}
            <CreatorModal />
            <DeleteAccountModal />
            <DeleteCreatorModal />
            <LogoutModal />
            <ComingSoonModal />
            <SupportModal />
            <TermsModal />
            <EchoDetailsModal />
            <NoCreditsModal />
            <StripeRequiredModal />
            <AlertModal />
            <WelcomeModal />
          </ToastProvider>
        </EchoProvider>
      </AuthProvider>
    </QueryClientProvider>
  );

  // Normal app with all routes. Google OAuth is optional for local/dev builds.
  return (
    <ErrorBoundary>
      {GOOGLE_CLIENT_ID ? (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{normalApp}</GoogleOAuthProvider>
      ) : (
        normalApp
      )}
    </ErrorBoundary>
  );
}

export default App;
